import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import type { AdvisoryAiOrgReportRow } from '@/features/advisory-ai/types'
import { formatDate } from '@/features/advisory-ai/utils'
import dayjs from 'dayjs'

export type CallStatus = 'needsAttention' | 'inProgress' | 'generating' | 'ready'

export function getCallStatus(row: AdvisoryAiOrgReportRow): CallStatus {
  const status = String(row.status || '').toUpperCase()
  if (status === AdvisoryCallStatus.PENDING || status === AdvisoryCallStatus.ERROR) {
    return 'needsAttention'
  }
  if (status === AdvisoryCallStatus.STARTED_BRIEF || status === AdvisoryCallStatus.COMPLETED_BRIEF) {
    return 'inProgress'
  }
  // Outputs are being produced. Both of these fell through to the catch-all below,
  // so a meeting mid-export was listed as needing attention when nothing was wrong.
  if (
    status === AdvisoryCallStatus.PREPARE_EXPORTING ||
    status === AdvisoryCallStatus.EXPORTING
  ) {
    return 'generating'
  }
  if (status === AdvisoryCallStatus.COMPLETED_EXPORT) {
    return 'ready'
  }
  return 'needsAttention'
}

export function getDaysAway(startDate?: string | null): string {
  if (!startDate) return 'No date set'
  const days = dayjs(startDate).diff(dayjs(), 'day')
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days > 1) return `${days} days away`
  if (days === -1) return 'Yesterday'
  return `${Math.abs(days)} days ago`
}

export function isRealMeetingDate(meetingDate?: string | null): boolean {
  if (!meetingDate) return false
  const d = dayjs(meetingDate)
  return d.isValid()
}

export function isMeetingFinished(meetingDate?: string | null): boolean {
  if (!isRealMeetingDate(meetingDate)) return false
  return dayjs(meetingDate).isBefore(dayjs())
}

export function getMeetingDateDisplay(meetingDate?: string | null): string {
  if (!isRealMeetingDate(meetingDate)) return 'Meeting date not set yet'
  return dayjs(meetingDate).format('ddd D MMM').toUpperCase()
}

export function formatCallDate(startDate?: string | null): string {
  if (!startDate) return ''
  return dayjs(startDate).format('ddd D MMM').toUpperCase()
}

export function getStepProgress(row: AdvisoryAiOrgReportRow): { current: number; total: number; label: string } {
  const status = String(row.status || '').toUpperCase()
  if (status === AdvisoryCallStatus.PENDING) {
    return { current: 0, total: 5, label: 'Not started' }
  }
  if (status === AdvisoryCallStatus.STARTED_BRIEF) {
    return { current: 1, total: 5, label: 'Step 1 of 5 — Agenda' }
  }
  if (status === AdvisoryCallStatus.COMPLETED_BRIEF) {
    return { current: 3, total: 5, label: 'Step 3 of 5 — AI Analysis' }
  }
  if (
    status === AdvisoryCallStatus.PREPARE_EXPORTING ||
    status === AdvisoryCallStatus.EXPORTING
  ) {
    return { current: 4, total: 5, label: 'Step 4 of 5 — Generating outputs' }
  }
  if (status === AdvisoryCallStatus.COMPLETED_EXPORT) {
    return { current: 5, total: 5, label: 'All outputs ready' }
  }
  if (status === AdvisoryCallStatus.ERROR) {
    return { current: 0, total: 5, label: 'Error occurred' }
  }
  return { current: 0, total: 5, label: 'Not started' }
}

export function formatCallDateRange(row: AdvisoryAiOrgReportRow): string {
  return row.startDate && row.endDate ? `${formatDate(row.startDate)} to ${formatDate(row.endDate)}` : 'No date range set.'
}
